import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import moment from "moment";
import "moment/locale/pt-br";
import { Shell } from "../../common/shell/shell.page";
import { PageHeader } from "../../common/components/page-header";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import application from "../../core/application";
import { useModuleAccess } from "../../settings/module-access";
import { ModuleLockedView } from "../../settings/components/module-locked-view";
import { Permission } from "../../users/user.entity";
import { cashSessionRepository } from "../cash-session.repository";
import type { CashSession } from "../cash-session.entity";
import { CashRegisterSectionTabs } from "../components/cash-register-section-tabs";
import { CashRegisterReportHero } from "../components/cash-register-report-hero";

const money = (n: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);

export function CashRegisterReportsPage() {
  application.useHasPermission(Permission.CASH_REGISTER_MANAGEMENT);
  const theme = useTheme();
  const moduleAccess = useModuleAccess();
  const featureOn = !moduleAccess.loading && moduleAccess.isEnabled("cash_register");

  const [sessions, setSessions] = useState<CashSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await cashSessionRepository.listRecentSessions(80);
      setSessions(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível carregar as sessões.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!featureOn) return;
    load();
  }, [featureOn, load]);

  const byDay = useMemo(() => {
    const m = new Map<string, CashSession[]>();
    for (const s of sessions) {
      const k = s.getDayKey();
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(s);
    }
    return Array.from(m.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [sessions]);

  const openCount = useMemo(
    () => sessions.filter((s) => s.getStatus() === "open").length,
    [sessions]
  );

  if (moduleAccess.loading) {
    return (
      <Shell title="Relatórios de caixa">
        <LoadingOverlay message="Carregando módulo…" subtle />
      </Shell>
    );
  }

  if (!featureOn) {
    return (
      <Shell title="Relatórios de caixa">
        <PageHeader title="Relatórios de caixa" description="Histórico e conferência por turno." />
        <ModuleLockedView
          moduleName="Caixa"
          groupId="cash_register"
          details="Ative o módulo Caixa para ver o histórico de sessões e relatórios por dia."
          features={["Histórico completo de sessões", "Relatório diário de caixa", "Resumo de receitas por turno"]}
        />
      </Shell>
    );
  }

  return (
    <Shell title="Relatórios de caixa">
      {loading ? <LoadingOverlay message="Carregando sessões…" subtle /> : null}

      <PageHeader
        title="Relatórios de caixa"
        description="Sessões agrupadas por dia civil. Abra o detalhe para conferência de dinheiro e vendas daquele turno."
      />

      <CashRegisterSectionTabs />

      <Stack spacing={3}>
        <CashRegisterReportHero
          title="Histórico e conferência"
          chips={
            <>
              <Chip
                icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />}
                label={`${sessions.length} sessões listadas`}
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              />
              {openCount > 0 ? (
                <Chip
                  label={`${openCount} aberta(s)`}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              ) : null}
            </>
          }
        />

        {error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}

        {byDay.length === 0 && !loading ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 3,
              border: "1px dashed",
              borderColor: "divider",
              bgcolor: alpha(theme.palette.primary.main, 0.03),
            }}
          >
            <Typography color="text.secondary">
              Nenhuma sessão registrada ainda. Abra o caixa na aba Operação.
            </Typography>
          </Paper>
        ) : null}

        {byDay.map(([dayKey, daySessions]) => (
          <Box key={dayKey}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}
            >
              {moment(dayKey, "YYYY-MM-DD").format("dddd, D [de] MMMM [de] YYYY")}
            </Typography>
            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                        "& .MuiTableCell-head": { fontWeight: 700, fontSize: 13 },
                      }}
                    >
                      <TableCell>Status</TableCell>
                      <TableCell>Abertura</TableCell>
                      <TableCell align="right">Dinheiro inicial</TableCell>
                      <TableCell>Fechamento</TableCell>
                      <TableCell align="right">Valor contado</TableCell>
                      <TableCell align="right" width={160}>
                        Ações
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {daySessions.map((s) => {
                      const closed = s.getStatus() === "closed";
                      return (
                        <TableRow key={s.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                          <TableCell>
                            <Chip
                              size="small"
                              label={closed ? "Fechado" : "Aberto"}
                              color={closed ? "default" : "warning"}
                              variant={closed ? "outlined" : "filled"}
                              sx={{ fontWeight: 700 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {moment(s.getOpenedAt()).format("HH:mm")}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {moment(s.getOpenedAt()).format("LL")}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{money(s.getOpeningCash())}</TableCell>
                          <TableCell>
                            {closed && s.getClosedAt() ? (
                              <>
                                <Typography variant="body2" fontWeight={600}>
                                  {moment(s.getClosedAt()!).format("HH:mm")}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {moment(s.getClosedAt()!).format("LL")}
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {typeof s.getClosingCash() === "number" ?
                              money(s.getClosingCash()!) :
                              "—"}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              component={Link}
                              to={`/caixa/sessao/${s.id}`}
                              size="small"
                              variant="contained"
                              endIcon={<ChevronRightIcon />}
                              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                            >
                              Ver detalhes
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Box>
        ))}
      </Stack>
    </Shell>
  );
}
